<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="dialog" content-class="conversation-downloader" persistent width="400" scrollable>
    <v-card>
      <v-card-title class="sms-primary-area">
        {{ dialogTitle }}
      </v-card-title>
      <v-card-text class="card-content custom-scrollbar">
        <div class="download-description">
          {{ dialogDescription }}
        </div>

        <div class="sms-file-type-selection">
          <v-select
            ref="fileType"
            v-model="reportFormat"
            :label="$t('reportFormatLabel')"
            :items="reportFormatList"
            item-text="name"
            item-value="value"
            min-width="290px"
          >
          </v-select>
        </div>

        <div v-show="!showCustom">
          <v-hover v-slot:default="{ hover }">
            <div
              v-ripple
              class="selection-box"
              :class="{
                'elevation-3': hover || isNMonthRange(1),
                selected: isNMonthRange(1),
                'black--text': isHighContrastThemeActive && isNMonthRange(1)
              }"
              @click="select(1)"
            >
              <div class="description">{{ $t('quickSelectionCard.last') }}</div>
              <div class="range">30</div>
              <div class="range-units">{{ $t('quickSelectionCard.days') }}</div>
            </div>
          </v-hover>

          <v-hover v-slot:default="{ hover }">
            <div
              v-ripple
              class="selection-box"
              :class="{
                'elevation-3': hover || isNMonthRange(2),
                selected: isNMonthRange(2),
                'black--text': isHighContrastThemeActive && isNMonthRange(2)
              }"
              @click="select(2)"
            >
              <div class="description">{{ $t('quickSelectionCard.last') }}</div>
              <div class="range">60</div>
              <div class="range-units">{{ $t('quickSelectionCard.days') }}</div>
            </div>
          </v-hover>

          <v-hover v-slot:default="{ hover }">
            <div
              v-ripple
              class="selection-box"
              :class="{
                'elevation-3': hover || isNMonthRange(3),
                selected: isNMonthRange(3),
                'black--text': isHighContrastThemeActive && isNMonthRange(3)
              }"
              @click="select(3)"
            >
              <div class="description">{{ $t('quickSelectionCard.last') }}</div>
              <div class="range">90</div>
              <div class="range-units">{{ $t('quickSelectionCard.days') }}</div>
            </div>
          </v-hover>

          <v-hover v-slot:default="{ hover }">
            <div
              v-ripple
              class="selection-box"
              :class="{
                'elevation-3': hover || isNMonthRange(6),
                selected: isNMonthRange(6),
                'black--text': isHighContrastThemeActive && isNMonthRange(6)
              }"
              @click="select(6)"
            >
              <div class="description">{{ $t('quickSelectionCard.last') }}</div>
              <div class="range">6</div>
              <div class="range-units">{{ $t('quickSelectionCard.months') }}</div>
            </div>
          </v-hover>

          <v-hover v-slot:default="{ hover }">
            <div
              v-ripple
              class="selection-box"
              :class="{
                'elevation-3': hover || isNMonthRange(12),
                selected: isNMonthRange(12),
                'black--text': isHighContrastThemeActive && isNMonthRange(12)
              }"
              @click="select(12)"
            >
              <div class="description">{{ $t('quickSelectionCard.last') }}</div>
              <div class="range">12</div>
              <div class="range-units">{{ $t('quickSelectionCard.months') }}</div>
            </div>
          </v-hover>

          <v-hover v-slot:default="{ hover }">
            <div
              v-ripple
              class="selection-box"
              :class="{
                'elevation-3': hover || isNMonthRange(18),
                selected: isNMonthRange(18),
                'black--text': isHighContrastThemeActive && isNMonthRange(18)
              }"
              @click="select(18)"
            >
              <div class="description">{{ $t('quickSelectionCard.last') }}</div>
              <div class="range">18</div>
              <div class="range-units">{{ $t('quickSelectionCard.months') }}</div>
            </div>
          </v-hover>

          <v-btn text outlined class="custom-range-btn" @click="showCustom = true">
            {{ $t('customRange') }}
          </v-btn>
        </div>

        <div class="selection-box wide" v-show="showCustom">
          <div class="range">{{ $t('customRange') }}</div>

          <v-menu
            ref="menuFrom"
            v-model="menuFrom"
            :close-on-content-click="false"
            transition="scale-transition"
            min-width="290px"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateRange.from"
                :label="$t('fromLabel')"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              ref="pickerFrom"
              v-model="dateRange.from"
              @change="saveFrom"
              :locale="getSettings.displayPreferences.language"
            />
          </v-menu>

          <v-menu
            ref="menuTo"
            v-model="menuTo"
            :close-on-content-click="false"
            transition="scale-transition"
            min-width="290px"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="dateRange.to"
                :label="$t('toLabel')"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              ref="pickerTo"
              v-model="dateRange.to"
              @change="saveTo"
              :locale="getSettings.displayPreferences.language"
            />
          </v-menu>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn v-show="showCustom" class="back-btn" text @click="showCustom = false">
          {{ $t('buttons.back') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn @click="cancel()" class="mr-3" text>
          {{ $t('buttons.cancel') }}
        </v-btn>

        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              :loading="downloading"
              :disabled="downloading"
              v-on="on"
              class="sms-primary-button"
              depressed
              @click="confirm"
            >
              {{ $t('buttons.download') }}
            </v-btn>
          </template>
          <span>
            {{ $t('from', { date: $moment(this.dateRange.from).format('YYYY-MM-DD') }) }}
            {{ $t('to', { date: $moment(this.dateRange.to).format('YYYY-MM-DD') }) }}
          </span>
        </v-tooltip>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ThemeMixin from '@/mixins/ThemeMixin';
import { mapActions, mapGetters } from 'vuex';
import ReportService from '@/api/services/ReportService';

export default {
  name: 'SmsConversationDownloaderDialog',

  mixins: [ThemeMixin],

  data() {
    return {
      dialog: false,
      downloading: false,
      reportFormat: 'CSV',
      reportFormatList: [
        { name: 'Comma Separated (.csv)', value: 'CSV' },
        { name: 'JSON (.json)', value: 'JSON' },
        { name: 'Microsoft Word (.docx)', value: 'WORD' }
      ],
      showCustom: false,
      options: {
        messagesMode: false
      },
      dateRange: {
        from: null,
        to: null
      },
      menuFrom: false,
      menuTo: false
    };
  },

  computed: {
    ...mapGetters('user', ['getUser']),
    ...mapGetters('conversationsV2', ['activeConversation']),
    ...mapGetters('preferences', ['getSettings']),
    ...mapGetters('contactsV2', ['getContactByNumber']),

    conversationDisplayName() {
      let name = '';
      if (this.activeConversation && this.activeConversation.contact) {
        name = this.activeConversation.contact.displayName;
      }
      return name;
    },

    dialogTitle() {
      return this.options.messagesMode ? this.$t('messagesMode.title') : this.$t('title');
    },

    dialogDescription() {
      return this.options.messagesMode
        ? this.$t('messagesMode.description')
        : this.$t('description', { name: this.conversationDisplayName });
    }
  },

  created() {
    this.initialize();
  },

  methods: {
    ...mapActions('conversationsV2', ['retrieveConversationsByDateRange']),

    open(options) {
      this.initialize(options);
      this.dialog = true;
      return new Promise((resolve, reject) => {
        this.$once('download-confirmed', resolve);
        this.$once('download-cancelled', reject);
      });
    },

    confirm() {
      const params = {
        from: this.$moment(this.dateRange.from).format('YYYY-MM-DD HH:MM:SS'),
        to: this.$moment(this.dateRange.to).endOf('day').format('YYYY-MM-DD HH:MM:SS'),
        number: this.options.messagesMode ? null : this.activeConversation.contact.number
      };
      this.downloading = true;

      if (this.options.messagesMode) {
        ReportService.getAllMessagesForCurrentUser(params.from, params.to, this.reportFormat).then(response =>
          this.handleResponse(response)
        );
      } else {
        ReportService.getMessagesToNumberForCurrentUser(
          params.number,
          params.from,
          params.to,
          this.reportFormat
        ).then(response => this.handleResponse(response));
      }
    },

    handleResponse({ data, mimeType }) {
      let fileName, fileTypeExtension, blob;

      switch (mimeType) {
        case 'text/csv':
          fileTypeExtension = 'csv';
          break;
        case 'application/json':
          fileTypeExtension = 'json';
          break;
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          fileTypeExtension = 'docx';
          break;
        default:
          fileTypeExtension = 'txt';
          break;
      }
      fileName = this.$t('fileNameOutput', {
        from: this.$moment(this.dateRange.from).format('YYYY-MM-DD'),
        to: this.$moment(this.dateRange.to).format('YYYY-MM-DD'),
        ext: fileTypeExtension
      });
      blob = new Blob([data], { type: mimeType });

      // Download the file
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveBlob(blob, fileName);
      } else {
        const a = window.document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
      }

      // UI Clean-up
      this.dialog = false;
      this.downloading = false;
      this.$emit('download-confirmed');
      this.$off('download-cancelled');
    },

    select(months) {
      this.dateRange.from = this.$moment().subtract(months, 'months').format('YYYY-MM-DD');
      this.dateRange.to = this.$moment().format('YYYY-MM-DD');
    },

    saveFrom(date) {
      this.$refs.menuFrom.save(date);
    },

    saveTo(date) {
      this.$refs.menuTo.save(date);
    },

    isNMonthRange(nMonths) {
      const from = this.$moment().subtract(nMonths, 'months').format('YYYY-MM-DD');
      const to = this.$moment().format('YYYY-MM-DD');
      return this.dateRange.from === from && this.dateRange.to === to;
    },

    cancel() {
      this.dialog = false;
      this.downloading = false;
      this.$emit('download-cancelled', 'USER_ACTION:CANCEL');
      this.$off('download-confirmed');
    },

    initialize(options = {}) {
      this.dateRange.from = this.$moment().subtract(1, 'months').format('YYYY-MM-DD');
      this.dateRange.to = this.$moment().format('YYYY-MM-DD');
      this.showCustom = false;
      this.options = options;
    }
  }
};
</script>

<style>
.sms-file-type-selection .v-text-field .v-label--active {
  top: -1px !important;
}
</style>

<style src="./scoped.css" scoped />
